import { useCallback, useEffect, useState } from 'react';

// material-ui
import { Button, Checkbox, Divider, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';

// project imports
import SubCard from 'ui-component/cards/SubCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { gridSpacing } from 'store/constant';
import UserService from 'services/UserService';
import useAuth from 'hooks/useAuth';
import User from 'types/user';
import { useTranslation } from 'react-i18next';
import Utilities from 'utils/utilities';
import { dispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import GenericSkeleton from 'ui-component/skeleton/GenericSkeleton';
import temperatureConfList from 'types/temperatureConf';
import SelectOption from 'types/selectOption';

const UserProfile = () => {
    const [currentUser, setCurrentUser] = useState<User>();
    const { user: loggedUser } = useAuth();
    const userService = new UserService();
    const utilities = new Utilities();
    const { t } = useTranslation();
    const temperatureConf: SelectOption[] = temperatureConfList();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        (async () => {
            if (loggedUser) {
                await fetchUser();
            }
        })();
    }, [loggedUser]);

    const fetchUser = async () => {
        setIsLoading(true);
        const user = await userService.getUser();
        const userCopy = {
            ...user!,
            otpEnabled: await userService.isOtpEnabled(user.username!)
        };
        setCurrentUser(userCopy);
        setIsLoading(false);
    };

    const onSave = async (user: User) => {
        setIsLoading(true);
        if (user && utilities.isNotNullOrEmpty(user.username!)) {
            const response = await userService.editUser(user);
            if (response) {
                dispatch(startSnackbar(t('USER_EDITED_SUCCESSFULLY'), 'success'));
                await fetchUser();
            }
        } else {
            dispatch(startSnackbar(t('EMAIL_IS_MANDATORY'), 'error'));
        }
        setIsLoading(false);
    };

    return !isLoading ? (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12}>
                <SubCard title={t('GENERAL_SETTINGS')}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={currentUser?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            name: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel> {t('SURNAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={currentUser?.surname}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            surname: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('SURNAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('Email')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={currentUser?.username}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            username: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('Email')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('TEMPERATURE_UNIT')}</InputLabel>
                                <Select
                                    label={t('TEMPERATURE_UNIT')}
                                    name="temperatureUnit"
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        const updatedUser = {
                                            ...currentUser!,
                                            preferredTemperatureUnit: newValue
                                        };
                                        setCurrentUser(updatedUser);
                                    }}
                                    sx={{ color: 'white' }}
                                    value={currentUser?.preferredTemperatureUnit!}
                                >
                                    {temperatureConf.map((temperature) => (
                                        <MenuItem value={temperature.value} key={temperature.value}>
                                            {t(temperature.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </SubCard>
            </Grid>
            <Grid item xs={12}>
                <SubCard title={t('ADVANCE_SETTINGS')}>
                    <Grid container direction="column" spacing={3} alignContent="flex-start">
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <>{t('ENABLE_OTP')}</>
                                <Checkbox
                                    disabled={currentUser?.isSystemAdmin}
                                    checked={currentUser?.otpEnabled || currentUser?.isSystemAdmin}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            otpEnabled: d.target.checked
                                        };
                                        setCurrentUser(e);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ mt: 2 }} />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 3 }}>
                        <Grid spacing={2} container justifyContent="flex-end">
                            <Grid item>
                                <AnimateButton>
                                    <Button onClick={() => onSave(currentUser!)} variant="contained">
                                        {t('UPDATE_PROFILE')}
                                    </Button>
                                </AnimateButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </SubCard>
            </Grid>
        </Grid>
    ) : (
        <GenericSkeleton />
    );
};

export default UserProfile;
